import React, { Fragment, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import Currency from 'react-currency-formatter';

// material-ui
import { Table, TableFooter, TableBody, TableCell, TableContainer, TableHead, TableRow, Grid } from '@mui/material';

// Icons

// Styles
import { makeStyles } from '@mui/styles';
import { useLanguageReference } from 'services/Hooks/useLanguageReference';
import { useProjectWalls, useProjetDealerCopy } from 'services/Data/ProjectServices';
import useImage from 'services/Hooks/useImage';
import { arrayUnique, flattenMap } from 'services/Utilities/Utilities';
import { ProjectInventoryItemAdjustmentTypeEnumValue } from 'enums/ProjectInventoryItemAdjustmentTypeEnum';
import InventoryItemCategoryEnum, { InventoryItemCategoryEnumValue } from 'enums/InventoryItemCategoryEnum';
import DealerCopyAdjustmentGroupedMerchandisingToolingSection from './DealerCopyAdjustmentGroupedMerchandisingToolingSection';
import DealerCopyAdjustmentGroupedProjectWallSection from './DealerCopyAdjustmentGroupedProjectWallSection';
import DealerCopyAdjustmentGroupedCeilingSection from './DealerCopyAdjustmentGroupedCeilingSection';
import DealerCopyAdjustmentDefaultCategorySection from './DealerCopyAdjustmentDefaultCategorySection';
import { MoneyFlag } from '@acnb/CustomComponents/MoneyAdornment';

// Actions

// Components

// Others

const useStyles = makeStyles(theme => ({
  //...customSelectStyle(theme),
  //...dashboardStyles(theme),
  //...tableStyles(theme),
  textCenter: {
    textAlign: 'center',
  },
  iconBigger: {
    fontSize: '20px',
  },
  adjustForMobile: {
    [theme.breakpoints.down('sm')]: {
      padding: '0 15px !important',
    },
    [theme.breakpoints.up('sm')]: {
      padding: '0',
    },
  },
  projectAdjustmentBottomBorder: {
    borderBottom: '0.15rem solid rgba(169, 169, 169, 1)',
  },
  projectAdjustmentLeftBorder: {
    borderLeft: '0.15rem solid rgba(169, 169, 169, 1)',
  },
  projectAdjustmentTopBorder: {
    borderTop: '0.15rem solid rgba(169, 169, 169, 1)',
  },
  projectAdjustmentRightBorder: {
    borderRight: '0.15rem solid rgba(169, 169, 169, 1)',
  },
}));

export const useHasDealerCopyProjectInventoryAdjustments = (project, adjustmentType) => {
  return useMemo(() => {
    if (!project?.projectInventoryItemAdjustments?.length) {
      return false;
    }

    return project.projectInventoryItemAdjustments.some(piia => piia.adjustmentType === adjustmentType);
  }, [adjustmentType, project?.projectInventoryItemAdjustments]);
};

export const DealerCopyProjectInventoryAdjustments = ({ project, currencyName, totalCol, adjustmentType }) => {
  const { t } = useTranslation();
  const classes = useStyles();
  const { labelReference, descriptionReference } = useLanguageReference();

  const { data: dealerCopyProjectInventoryItems, isLoading: gettingProjectInventoryItems } = useProjetDealerCopy(project.id);
  const { data: projectWalls } = useProjectWalls(project.id);
  const { getInventoryItemImage } = useImage();
  const hasAdjustmentRows = useHasDealerCopyProjectInventoryAdjustments(project, adjustmentType);

  const flatProjectInventoryItems = useMemo(() => flattenMap(dealerCopyProjectInventoryItems), [
    dealerCopyProjectInventoryItems,
  ]);

  const allAdditionalItemAdjustments = useMemo(
    () => project.projectInventoryItemAdjustments.filter(piia => piia.adjustmentType === adjustmentType),
    [adjustmentType, project.projectInventoryItemAdjustments],
  );

  const additionalItemAdjustments = useMemo(() => allAdditionalItemAdjustments.filter(piia => !piia.relatedItemKey), [
    allAdditionalItemAdjustments,
  ]);

  const packageAdditionalItemAdjustments = useMemo(() => allAdditionalItemAdjustments.filter(piia => piia.relatedItemKey), [
    allAdditionalItemAdjustments,
  ]);

  const packageAdditionalItemAdjustmentRelatedKeys = useMemo(() => {
    const relatedKeys = packageAdditionalItemAdjustments.map(i => i.relatedItemKey);

    return relatedKeys.filter((value, index, array) => {
      return array.indexOf(value) === index;
    });
  }, [packageAdditionalItemAdjustments]);

  if (gettingProjectInventoryItems || !hasAdjustmentRows) return null;

  const projectInventoryItemAjustmentCategories =
    additionalItemAdjustments?.length > 0
      ? arrayUnique(additionalItemAdjustments.map(a => a.inventoryItemCategoryEnum))
      : [];

  const additionalItemTotalQuantity = allAdditionalItemAdjustments?.reduce((acc, itemAdjustment) => {
    return (
      acc +
      (itemAdjustment.adjustmentType === ProjectInventoryItemAdjustmentTypeEnumValue.DealerInventory
        ? -itemAdjustment.quantity
        : itemAdjustment.quantity)
    );
  }, 0);

  let additionalItemTotalPrice =
    allAdditionalItemAdjustments?.length > 0
      ? allAdditionalItemAdjustments?.reduce((acc, itemAdjustment) => {
          return (
            acc +
            itemAdjustment.quantity *
              flatProjectInventoryItems?.find(pi => pi.inventoryItem_Id === itemAdjustment.inventoryItem_Id)
                ?.projectDealerUnitPrice
          );
        }, 0)
      : 0;

  return (
    <TableContainer>
      <Table sx={{ minWidth: 650 }} aria-label="simple table">
        <TableHead>
          <TableRow>
            <TableCell colSpan={2}>{t('image_info')}</TableCell>
            <TableCell>{t('sku')}</TableCell>
            <TableCell colSpan={2}>{t('description')}</TableCell>
            <TableCell align="center">{t('calculated_quantity')}</TableCell>
            <TableCell style={{ textAlign: 'center' }} width={'14%'}>
              <div>
                {t('unitPrice')}
                <MoneyFlag currencyCode={currencyName} />
              </div>
            </TableCell>
            <TableCell align="center">{t('total')}</TableCell>
            <TableCell>{t('package_price')}</TableCell>
          </TableRow>
        </TableHead>
        <TableBody>
          {projectInventoryItemAjustmentCategories &&
            projectInventoryItemAjustmentCategories.length > 0 &&
            projectInventoryItemAjustmentCategories.map(key => {
              const associatedProjectAdjustments = additionalItemAdjustments.filter(
                piia => piia.inventoryItemCategoryEnum === key,
              );

              const inventoryItemIds = associatedProjectAdjustments?.map(pa => pa.inventoryItem_Id);

              const projectInventoryItems = flatProjectInventoryItems.filter(
                aii => inventoryItemIds.includes(aii.inventoryItem_Id) && aii.quantity === 0,
              );

              const projectAdjustments = associatedProjectAdjustments.sort((a, b) =>
                `${projectInventoryItems.find(pii => pii.inventoryItem_Id === a.inventoryItem_Id)?.sku}`.localeCompare(
                  `${projectInventoryItems.find(pii => pii.inventoryItem_Id === b.inventoryItem_Id)?.sku}`,
                ),
              );

              let category = InventoryItemCategoryEnum.find(ii => ii.value === Number(key));
              let categoryLabel = category?.[labelReference];

              switch (category.value) {
                case InventoryItemCategoryEnumValue.MerchandisingTooling:
                  return (
                    <DealerCopyAdjustmentGroupedMerchandisingToolingSection
                      key={key}
                      categoryLabel={categoryLabel}
                      projectInventoryItems={projectInventoryItems || []}
                      headerColSpan={totalCol}
                      projectAdjustments={projectAdjustments}
                      currencyName={currencyName}
                    />
                  );
                case InventoryItemCategoryEnumValue.MerchandisingDisplay:
                  return (
                    <DealerCopyAdjustmentGroupedProjectWallSection
                      key={key}
                      categoryLabel={categoryLabel}
                      projectInventoryItems={projectInventoryItems || []}
                      projectWalls={projectWalls}
                      headerColSpan={totalCol}
                      projectAdjustments={projectAdjustments}
                      currencyName={currencyName}
                    />
                  );
                case InventoryItemCategoryEnumValue.CeilingElement:
                  return (
                    <DealerCopyAdjustmentGroupedCeilingSection
                      key={key}
                      categoryLabel={categoryLabel}
                      projectInventoryItems={projectInventoryItems || []}
                      ceilingId={project.ceiling_Id}
                      headerColSpan={totalCol}
                      projectAdjustments={projectAdjustments}
                      currencyName={currencyName}
                    />
                  );
                default:
                  return (
                    <DealerCopyAdjustmentDefaultCategorySection
                      key={key}
                      categoryLabel={categoryLabel}
                      projectInventoryItems={projectInventoryItems || []}
                      headerColSpan={totalCol}
                      projectAdjustments={projectAdjustments}
                      currencyName={currencyName}
                    />
                  );
              }
            })}

          {packageAdditionalItemAdjustmentRelatedKeys?.length > 0 && (
            <>
              <TableRow>
                <TableCell colSpan={9} style={{ backgroundColor: '#c9c7c7' }}>
                  <strong>{t('packages')}</strong>
                </TableCell>
              </TableRow>

              {packageAdditionalItemAdjustmentRelatedKeys.map(relateKey => {
                const relatedKeyAdditionalItemAdjustments = packageAdditionalItemAdjustments.filter(
                  ai => ai.relatedItemKey === relateKey,
                );

                const relatedAdditionalItemAdjustmentCategories = arrayUnique(
                  relatedKeyAdditionalItemAdjustments.map(ad => ad.inventoryItemCategoryEnum),
                );

                const totalRowSpan =
                  (relatedKeyAdditionalItemAdjustments?.length || 0) +
                  (relatedAdditionalItemAdjustmentCategories?.length || 0);

                return relatedAdditionalItemAdjustmentCategories
                  .sort((a, b) => a - b)
                  .map((categoryEnum, catIndex) => {
                    let category = InventoryItemCategoryEnum.find(ii => ii.value === Number(categoryEnum));
                    let categoryLabel = category?.[labelReference];

                    const currentProjectAdjustmentItemCategoryItems = relatedKeyAdditionalItemAdjustments?.filter(
                      i => i.inventoryItemCategoryEnum === categoryEnum,
                    );

                    const isLastCategory = relatedAdditionalItemAdjustmentCategories.length - 1 === catIndex;

                    return (
                      <Fragment key={categoryEnum}>
                        <TableRow>
                          {catIndex === 0 && (
                            <TableCell
                              className={`${classes.projectAdjustmentBottomBorder} ${classes.projectAdjustmentTopBorder} ${classes.projectAdjustmentLeftBorder}`}
                              rowSpan={totalRowSpan}>
                              <strong>
                                {t('package', {
                                  packageNumber: packageAdditionalItemAdjustmentRelatedKeys.indexOf(relateKey) + 1,
                                })}
                              </strong>
                            </TableCell>
                          )}

                          {categoryEnum === InventoryItemCategoryEnumValue.Graphic ? (
                            <TableCell
                              className={
                                catIndex === 0
                                  ? `${classes.projectAdjustmentTopBorder} ${classes.projectAdjustmentRightBorder}`
                                  : classes.projectAdjustmentRightBorder
                              }
                              colSpan={8}
                              style={{ backgroundColor: '#c9c7c7' }}>
                              <strong>{categoryLabel}</strong>
                            </TableCell>
                          ) : (
                            <TableCell
                              className={
                                catIndex === 0
                                  ? `${classes.projectAdjustmentTopBorder} ${classes.projectAdjustmentRightBorder}`
                                  : classes.projectAdjustmentRightBorder
                              }
                              colSpan={7}
                              style={{ maxHeight: 0, height: 0, padding: 0 }}></TableCell>
                          )}
                        </TableRow>

                        {currentProjectAdjustmentItemCategoryItems?.map((projectAjustment, itemIndex) => {
                          const projectInventoryItem = flatProjectInventoryItems?.find(
                            aii => aii.inventoryItem_Id === projectAjustment.inventoryItem_Id,
                          );

                          if (!projectInventoryItem) {
                            return null;
                          }

                          const currentUnitPrice = projectInventoryItem?.projectDealerUnitPrice || 0;

                          const currentTotalPrice = projectAjustment.quantity * currentUnitPrice;

                          const isLastItem = currentProjectAdjustmentItemCategoryItems.length - 1 === itemIndex;

                          const isLastCategoryLastItem = isLastCategory && isLastItem;

                          return (
                            <TableRow key={projectAjustment.id}>
                              <TableCell className={isLastCategoryLastItem ? classes.projectAdjustmentBottomBorder : ''}>
                                {projectInventoryItem?.files?.length > 0 && (
                                  <img
                                    src={getInventoryItemImage(
                                      projectInventoryItem?.inventoryItem_Id,
                                      projectInventoryItem?.files[0].id,
                                    )}
                                    alt={t('InventoryItemImage')}
                                    style={{
                                      maxHeight: '50px',
                                      width: '100%',
                                      objectFit: 'contain',
                                    }}
                                  />
                                )}
                              </TableCell>

                              <TableCell className={isLastCategoryLastItem ? classes.projectAdjustmentBottomBorder : ''}>
                                <Grid alignItems={'center'}>
                                  <strong>{projectInventoryItem?.sku || ''}</strong>
                                </Grid>
                              </TableCell>
                              <TableCell
                                colSpan={2}
                                className={isLastCategoryLastItem ? classes.projectAdjustmentBottomBorder : ''}>
                                {projectInventoryItem?.[descriptionReference]}
                              </TableCell>
                              <TableCell
                                align="center"
                                className={isLastCategoryLastItem ? classes.projectAdjustmentBottomBorder : ''}>
                                {adjustmentType === ProjectInventoryItemAdjustmentTypeEnumValue.DealerInventory
                                  ? Math.abs(projectAjustment.quantity)
                                  : projectAjustment.quantity}
                              </TableCell>
                              <TableCell
                                align="right"
                                className={isLastCategoryLastItem ? classes.projectAdjustmentBottomBorder : ''}>
                                <Currency quantity={currentUnitPrice} currency={currencyName} />
                              </TableCell>

                              <TableCell
                                align="right"
                                className={isLastCategoryLastItem ? classes.projectAdjustmentBottomBorder : ''}>
                                <Currency quantity={currentTotalPrice} currency={currencyName} />
                              </TableCell>
                              <TableCell
                                align="center"
                                className={`${classes.projectAdjustmentRightBorder} ${
                                  isLastCategoryLastItem ? classes.projectAdjustmentBottomBorder : ''
                                }`}>
                                <Currency quantity={currentTotalPrice} currency={currencyName} />
                              </TableCell>
                            </TableRow>
                          );
                        })}
                      </Fragment>
                    );
                  });
              })}
            </>
          )}
        </TableBody>
        <TableFooter>
          <TableRow>
            <TableCell colSpan={totalCol}></TableCell>
          </TableRow>
          <TableRow>
            <TableCell colSpan={2}></TableCell>
            <TableCell></TableCell>
            <TableCell colSpan={2}></TableCell>
            <TableCell align="center">{additionalItemTotalQuantity}</TableCell>
            <TableCell></TableCell>
            <TableCell align="right">
              <Currency quantity={additionalItemTotalPrice ?? 0} currency={currencyName} />
            </TableCell>
            <TableCell></TableCell>
          </TableRow>
        </TableFooter>
      </Table>
    </TableContainer>
  );
};

export default DealerCopyProjectInventoryAdjustments;
